import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { clearLoginData } from "../session/utils/userSessionStorage";
import { finalizeSessionOnClient } from "../session/utils/sessionFinalize";
import { apiFetch } from "../../shared/utils/http";
import { logger } from "../../shared/utils/logger.client";

export interface UserData {
  firstName: string;
  lastName: string;
  PublicServiceNumber: string;
  BirthDate: string;
  email: string;
}

interface ManualAuthContextType {
  isAuthenticated: boolean;
  login: (payload: UserData) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  decodeToken: () => Promise<UserData | null>;
  user: UserData | null;
}

const ManualAuthContext = createContext<ManualAuthContextType | undefined>(undefined);

const logPrefix = "[ManualAuth]";
const timeNow = () => performance.now();
const dur = (t0: number) => `${(performance.now() - t0).toFixed(1)}ms`;

async function readJsonSafe(resp: Response): Promise<any | null> {
  const ct = (resp.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    logger.warn(`${logPrefix} Expected JSON but got content-type="${ct}" (possible SPA fallback)`);
    return null;
  }
  if (resp.status === 204) return null;
  try {
    return await resp.json();
  } catch (e) {
    logger.error(`${logPrefix} JSON parse failed:`, e);
    return null;
  }
}

export const ManualAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const navigate = useNavigate();

  const fetchManualToken = useCallback(async (): Promise<string | null> => {
    const t0 = timeNow();
    const csrfToken = localStorage.getItem("csrfToken");
    logger.debug(`${logPrefix} GET /session/token (begin)`, { hasCsrf: !!csrfToken });

    try {
      const res = await apiFetch("/session/token", {
        method: "GET",
        credentials: "include",
        headers: { "x-csrf-token": csrfToken ?? "" },
      }, { skipLogoutOn401: true });

      logger.debug(`${logPrefix} GET /session/token (resp)`, {
        status: res.status,
        statusText: res.statusText,
        ct: res.headers.get("content-type") || "",
        dur: dur(t0),
      });

      if (!res.ok) return null;

      const data = await readJsonSafe(res);
      if (!data || !data.token) {
        logger.warn(`${logPrefix} /session/token missing token`);
        return null;
      }

      const token: string = data.token;
      const [, payload] = token.split(".");
      const decoded = JSON.parse(atob(payload)) as UserData;
      setUser(decoded);
      return token;
    } catch (err) {
      logger.error(`${logPrefix} /session/token failed:`, err);
      return null;
    }
  }, []);

  const fetchSessionStatus = useCallback(async () => {
    const t0 = timeNow();
    logger.debug(`${logPrefix} GET /session/status (begin)`);
    try {
      const res = await apiFetch("/session/status", { credentials: "include" }, { skipLogoutOn401: true });
      logger.debug(`${logPrefix} GET /session/status (resp)`, {
        status: res.status,
        statusText: res.statusText,
        ct: res.headers.get("content-type") || "",
        dur: dur(t0),
      });
      if (!res.ok) return { authenticated: false as const };
      const data = await readJsonSafe(res);
      return (data ?? { authenticated: false }) as { authenticated: boolean; user?: UserData };
    } catch (e) {
      logger.error(`${logPrefix} /session/status error:`, e);
      return { authenticated: false as const };
    }
  }, []);

  const decodeToken = useCallback(async (): Promise<UserData | null> => {
    try {
      const t0 = timeNow();
      const token = await fetchManualToken();
      if (!token) return null;
      const [, payload] = token.split(".");
      const decoded = JSON.parse(atob(payload));
      const expired = decoded.exp && decoded.exp * 1000 < Date.now();
      logger.debug(`${logPrefix} decodeToken()`, { expired, dur: dur(t0) });
      return expired ? null : (decoded as UserData);
    } catch (err) {
      logger.error(`${logPrefix} decodeToken failed:`, err);
      return null;
    }
  }, [fetchManualToken]);

  useEffect(() => {
    (async () => {
      logger.debug("[ManualAuth] boot: checking existing session/token");
      const token = await fetchManualToken();
      if (token) {
        setIsAuthenticated(true);
        localStorage.setItem("loggedMethod", "MANUAL");
        logger.info("[ManualAuth] existing token detected");
        return;
      }

      const status = await fetchSessionStatus();
      if (status.authenticated) {
        setIsAuthenticated(true);
        setUser(status.user ?? null);
        localStorage.setItem("loggedMethod", "MANUAL");
        logger.info("[ManualAuth] existing server session detected");
      } else {
        setIsAuthenticated(false);
        setUser(null);
        logger.warn("[ManualAuth] no existing session/token");
      }
    })();
  }, [fetchManualToken, fetchSessionStatus]);

  const login = async (payload: UserData): Promise<void> => {
    const t0 = timeNow();
    const presence = {
      firstName: !!payload.firstName,
      lastName: !!payload.lastName,
      PublicServiceNumber: !!payload.PublicServiceNumber,
      BirthDate: !!payload.BirthDate,
      email: !!payload.email,
    };
    logger.info(`${logPrefix} POST /manual/login (begin)`, presence);

    try {
      const response = await fetch("/manual/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const ct = response.headers.get("content-type") || "";
      logger.debug(`${logPrefix} POST /manual/login (resp)`, {
        status: response.status,
        statusText: response.statusText,
        ct,
        dur: dur(t0),
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        const short = txt?.slice(0, 300);
        logger.error(`${logPrefix} /manual/login not OK`, { status: response.status, body: short });
        throw new Error(`Login failed (${response.status})`);
      }

      const statusT0 = timeNow();
      const status = await fetchSessionStatus();
      logger.debug(`${logPrefix} verify /session/status`, { authenticated: status.authenticated, dur: dur(statusT0) });
      if (!status.authenticated) throw new Error("Session not established");

      const finT0 = timeNow();
      await finalizeSessionOnClient().catch((e) => {
        logger.warn(`${logPrefix} finalizeSessionOnClient warning`, e);
      });
      logger.debug(`${logPrefix} finalizeSessionOnClient done`, { dur: dur(finT0) });

      localStorage.setItem("loggedMethod", "MANUAL");
      setIsAuthenticated(true);
      setUser(status.user ?? null);

      logger.info(`${logPrefix} login complete`, { dur: dur(t0) });
    } catch (error) {
      logger.error(`${logPrefix} login failed`, error);
      throw error;
    }
  };

  const handleLogout = async (): Promise<void> => {
    const t0 = timeNow();
    logger.info(`${logPrefix} POST /session/logout (begin)`);
    try {
      const res = await apiFetch("/session/logout", {
        method: "POST",
        credentials: "include",
      }, { skipLogoutOn401: true });
      logger.debug(`${logPrefix} POST /session/logout (resp)`, {
        status: res.status,
        statusText: res.statusText,
        dur: dur(t0),
      });
    } catch (err) {
      logger.error(`${logPrefix} logout failed`, err);
    } finally {
      clearLoginData();
      localStorage.removeItem("loggedMethod");
      setIsAuthenticated(false);
      setUser(null);
      logger.info(`${logPrefix} logout complete`);
    }
  };

  return (
      <ManualAuthContext.Provider
          value={{
            isAuthenticated,
            login,
            logout: handleLogout,
            getToken: fetchManualToken,
            decodeToken,
            user,
          }}
      >
        {children}
      </ManualAuthContext.Provider>
  );
};

export const useManualAuth = (): ManualAuthContextType => {
  const ctx = useContext(ManualAuthContext);
  if (!ctx) throw new Error("useManualAuth must be used within ManualAuthProvider");
  return ctx;
};

export const getToken = async (): Promise<string | null> => {
  const t0 = timeNow();
  const csrfToken = localStorage.getItem("csrfToken");
  logger.debug(`${logPrefix} ext GET /session/token (begin)`, { hasCsrf: !!csrfToken });

  try {
    const res = await apiFetch("/session/token", {
      method: "GET",
      credentials: "include",
      headers: { "x-csrf-token": csrfToken ?? "" },
    }, { skipLogoutOn401: true });

    logger.debug(`${logPrefix} ext GET /session/token (resp)`, {
      status: res.status,
      statusText: res.statusText,
      ct: res.headers.get("content-type") || "",
      dur: dur(t0),
    });

    if (!res.ok) return null;
    const data = await readJsonSafe(res);
    return data?.token || null;
  } catch (err) {
    logger.error(`${logPrefix} ext /session/token failed`, err);
    return null;
  }
};

export const manualLogout = async (): Promise<void> => {
  const t0 = timeNow();
  logger.info(`${logPrefix} ext POST /session/logout (begin)`);
  try {
    const res = await fetch("/session/logout", {
      method: "POST",
      credentials: "include",
    });
    logger.debug(`${logPrefix} ext POST /session/logout (resp)`, {
      status: res.status,
      statusText: res.statusText,
      dur: dur(t0),
    });
  } catch (err) {
    logger.error(`${logPrefix} ext logout failed`, err);
  } finally {
    clearLoginData();
    localStorage.removeItem("loggedMethod");
    logger.info(`${logPrefix} ext logout complete`);
  }
};
